import { Canvas, useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import type { Group } from "three";
import {
  BUILDING_INFO,
  BUILDING_LEVEL_MAX_MVP,
  buildingPower,
  buildingPrimaryStat,
  buildingSecondaryStat,
  buildingUpgradeCost,
  PRODUCTION_BUILDING_FAMILIES,
  type BuildingFamily,
} from "@stahlfront/core";
import type { ConstructionJobDto } from "@stahlfront/shared";
import { BuildingMesh } from "../scenes/buildingMeshes.js";
import { useStore } from "../store.js";

/** §6.1 building info popup — opens when a building is clicked on the base scene. */

interface BipProps {
  /** Called when the user opens the production panel from the action ring. */
  readonly onOpenProduction: (buildingId: string) => void;
  /** Called when the user opens the research tree from the RESEARCH_CENTER ring. */
  readonly onOpenResearch: () => void;
}

const ABOUT_BASE_COST = 1000;

export function BuildingInfoPopup({ onOpenProduction, onOpenResearch }: BipProps): JSX.Element | null {
  const selectedId = useStore((s) => s.selectedBuildingId);
  const buildings = useStore((s) => s.buildings);
  const setSelected = useStore((s) => s.setSelectedBuildingId);
  const constructionJobs = useStore((s) => s.constructionJobs);
  const setUpgradeConfirmBuildingId = useStore((s) => s.setUpgradeConfirmBuildingId);
  const setSpeedupConfirm = useStore((s) => s.setSpeedupConfirm);
  const [aboutOpen, setAboutOpen] = useState(false);

  const building = useMemo(
    () => buildings.find((b) => b.id === selectedId),
    [buildings, selectedId],
  );

  const activeUpgrade = useMemo(
    () =>
      constructionJobs.find(
        (j) => j.baseBuildingId === selectedId && j.state === "active",
      ) ?? null,
    [constructionJobs, selectedId],
  );

  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setSelected(null);
        setAboutOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, setSelected]);

  if (!selectedId || !building) return null;

  const family = building.family as BuildingFamily;
  const meta = BUILDING_INFO[family];
  const accent = `oklch(58% 0.16 ${meta.accentHue})`;
  const accentSoft = `oklch(78% 0.04 ${meta.accentHue})`;
  const accentTitle = `oklch(36% 0.10 ${meta.accentHue})`;
  const primary = buildingPrimaryStat(family, building.level);
  const secondary = buildingSecondaryStat(family, building.level);

  const isProductionFamily = (PRODUCTION_BUILDING_FAMILIES as readonly BuildingFamily[]).includes(
    family,
  );
  const isMaxLevel = building.level >= BUILDING_LEVEL_MAX_MVP;

  const close = (): void => {
    setSelected(null);
    setAboutOpen(false);
  };

  const onUpgrade = (): void => {
    if (activeUpgrade || isMaxLevel) return;
    setUpgradeConfirmBuildingId(building.id);
  };

  const onInspect = (): void => {
    console.info("[stahlfront] inspect (free-look) lands in Phase 2");
  };

  const onSpeedupBanner = (jobId: string): void => {
    setSpeedupConfirm({ kind: "construction", jobId });
  };

  return (
    <div className="bip-backdrop" onPointerDown={close} role="dialog" aria-modal="true">
      <div
        className="bip-panel"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          ["--bip-accent" as string]: accent,
          ["--bip-accent-soft" as string]: accentSoft,
          ["--bip-accent-title" as string]: accentTitle,
        }}
      >
        <header className="bip-titlebar">
          <span className="bip-title-left">
            <span className="bip-i" aria-hidden="true">
              i
            </span>
            <span className="bip-title-name">{meta.displayName}</span>
            <span className="bip-title-lv">Lv {building.level}</span>
          </span>
          <button type="button" className="bip-close" aria-label="Close info popup" onClick={close}>
            ×
          </button>
        </header>

        <section className="bip-hero" aria-label={`${meta.displayName} 3D preview`}>
          <Canvas dpr={[1, 2]} camera={{ position: [4, 3, 4], fov: 32 }} gl={{ antialias: true }}>
            <color attach="background" args={["oklch(14% 0.02 55)"]} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 8, 4]} intensity={1.0} />
            <directionalLight position={[-4, 4, -3]} intensity={0.35} color={"#7ea5d4"} />
            <RotatingBuilding family={family} />
            <gridHelper args={[10, 10, "#221f1c", "#1a1714"]} position={[0, -0.02, 0]} />
          </Canvas>
        </section>

        <section className="bip-lore" aria-label="Lore + tech gating">
          <p className="bip-lore-paragraph">{meta.lore}</p>
          {meta.techGates.length > 0 ? (
            <ol className="bip-tech">
              {meta.techGates.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ol>
          ) : null}
        </section>

        <section className="bip-params" aria-label="Building parameters">
          <ParamRow label="Build level" value={`Lv ${building.level}`} />
          <ParamRow
            label={meta.primaryStatLabel}
            value={`${formatStat(primary)}${meta.primaryStatUnit ? ` ${meta.primaryStatUnit}` : ""}`}
          />
          <ParamRow
            label={meta.secondaryStatLabel}
            value={`${formatStat(secondary)}${meta.secondaryStatUnit ? ` ${meta.secondaryStatUnit}` : ""}`}
          />
          <ParamRow label="Base defence multiplier" value={`${meta.defenceMultPct}%`} />
        </section>

        <button
          type="button"
          className="bip-about"
          onClick={() => setAboutOpen(true)}
          aria-label="Open About this building"
        >
          About <span aria-hidden="true">→</span>
        </button>

        {activeUpgrade ? (
          <UpgradeBanner
            job={activeUpgrade}
            fromLevel={building.level}
            onSpeedup={onSpeedupBanner}
          />
        ) : null}

        <ActionRing
          family={family}
          isMaxLevel={isMaxLevel}
          isUpgrading={!!activeUpgrade}
          onUpgrade={onUpgrade}
          onInspect={onInspect}
          onOpenProduction={isProductionFamily ? () => onOpenProduction(building.id) : undefined}
          onOpenResearch={family === "RESEARCH_CENTER" ? onOpenResearch : undefined}
        />
      </div>

      {aboutOpen ? (
        <AboutLevelBreakdown
          family={family}
          currentLevel={building.level}
          onClose={() => setAboutOpen(false)}
        />
      ) : null}
    </div>
  );
}

function formatStat(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} M`;
  if (value >= 10_000) return `${(value / 1000).toFixed(1)} k`;
  return new Intl.NumberFormat("en-US").format(value);
}

interface ParamRowProps {
  readonly label: string;
  readonly value: string;
}
function ParamRow({ label, value }: ParamRowProps): JSX.Element {
  return (
    <div className="bip-param-row">
      <span className="bip-param-label">{label}</span>
      <span className="bip-param-value">{value}</span>
    </div>
  );
}

interface UpgradeBannerProps {
  readonly job: ConstructionJobDto;
  readonly fromLevel: number;
  readonly onSpeedup: (jobId: string) => void;
}
/**
 * §6.5 — banner above action ring while a building is mid-upgrade. Shows
 * Lv N -> N+1 + live ETA countdown. Phase 2.D: the Speed-Up button now
 * opens the diamond-charge confirm modal.
 */
function UpgradeBanner({ job, fromLevel, onSpeedup }: UpgradeBannerProps): JSX.Element {
  const [eta, setEta] = useState<string>(() => formatEta(job));
  useEffect(() => {
    const id = setInterval(() => setEta(formatEta(job)), 1000);
    return () => clearInterval(id);
  }, [job]);
  return (
    <div className="bip-upgrade-banner" role="status" aria-live="polite">
      <span className="bip-upgrade-banner-lv">
        Building level Lv {fromLevel} → Lv {job.targetLevel}
      </span>
      <span className="bip-upgrade-banner-eta">ETA {eta}</span>
      <button
        type="button"
        className="bip-upgrade-banner-speedup"
        onClick={() => onSpeedup(job.id)}
        aria-label="Open speed-up dialog"
        title="Spend diamonds to finish this upgrade now"
      >
        Speed-Up
      </button>
    </div>
  );
}

function formatEta(job: ConstructionJobDto): string {
  const remaining = Math.max(0, job.startedAtMs + job.durationMs - Date.now());
  if (remaining <= 0) return "0s";
  const sec = Math.ceil(remaining / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rsec = sec % 60;
  if (min < 60) return `${min}m ${rsec.toString().padStart(2, "0")}s`;
  const h = Math.floor(min / 60);
  const rmin = min % 60;
  return `${h}h ${rmin.toString().padStart(2, "0")}m`;
}

interface ActionRingProps {
  readonly family: BuildingFamily;
  readonly isMaxLevel: boolean;
  readonly isUpgrading: boolean;
  readonly onUpgrade: () => void;
  readonly onInspect: () => void;
  readonly onOpenProduction: (() => void) | undefined;
  readonly onOpenResearch: (() => void) | undefined;
}
function ActionRing({
  family,
  isMaxLevel,
  isUpgrading,
  onUpgrade,
  onInspect: _onInspect,
  onOpenProduction,
  onOpenResearch,
}: ActionRingProps): JSX.Element {
  const upgradeLabel = isUpgrading ? "Upgrading…" : isMaxLevel ? "Maxed" : "Upgrade";
  const upgradeTitle = isUpgrading
    ? "Already upgrading — wait for ETA"
    : isMaxLevel
      ? "Already at maximum level"
      : "Open upgrade flow";
  const upgradeDisabled = isUpgrading || isMaxLevel;

  if (onOpenResearch) {
    return (
      <nav className="bip-ring bip-ring-3" aria-label="Action ring">
        <RingButton
          ariaLabel="Info (active)"
          icon="i"
          label="Info"
          active
          onClick={() => undefined}
          title="Currently shown — info popup"
        />
        <RingButton
          ariaLabel={upgradeLabel}
          icon="▲"
          label={upgradeLabel}
          onClick={onUpgrade}
          disabled={upgradeDisabled}
          title={upgradeTitle}
        />
        <RingButton
          ariaLabel="Open research tree"
          icon="⬢"
          label="Open Research"
          onClick={onOpenResearch}
          title="Industrial Progress + future trees"
          accent
        />
      </nav>
    );
  }

  if (onOpenProduction) {
    return (
      <nav className="bip-ring bip-ring-3" aria-label="Action ring">
        <RingButton
          ariaLabel="Info (active)"
          icon="i"
          label="Info"
          active
          onClick={() => undefined}
          title="Currently shown — info popup"
        />
        <RingButton
          ariaLabel={upgradeLabel}
          icon="▲"
          label={upgradeLabel}
          onClick={onUpgrade}
          disabled={upgradeDisabled}
          title={upgradeTitle}
        />
        <RingButton
          ariaLabel={
            family === "BARRACKS"
              ? "Open production — barracks"
              : "Open production — resource factory"
          }
          icon="⚙"
          label="Open Production"
          onClick={onOpenProduction}
          title="Workshop slots, queue, catalog"
          accent
        />
      </nav>
    );
  }

  // Per spec §6.5: disabled buttons are omitted, never greyed. Inspect (free-look
  // orbit, Phase 2.E) and Skins (cosmetic picker, post-Phase-2) are not yet
  // implemented, so the ring shows just the verbs that are real: Info + Upgrade.
  // When Inspect lands, it lights up here without a layout shift since the arc
  // re-balances on button count.
  return (
    <nav className="bip-ring bip-ring-2" aria-label="Action ring">
      <RingButton
        ariaLabel="Info (active)"
        icon="i"
        label="Info"
        active
        onClick={() => undefined}
        title="Info popup"
      />
      <RingButton
        ariaLabel={upgradeLabel}
        icon="▲"
        label={upgradeLabel}
        onClick={onUpgrade}
        disabled={upgradeDisabled}
        title={upgradeTitle}
      />
    </nav>
  );
}

interface RingButtonProps {
  readonly icon: string;
  readonly label: string;
  readonly ariaLabel: string;
  readonly title: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly accent?: boolean;
  readonly onClick: () => void;
}
function RingButton({
  icon,
  label,
  ariaLabel,
  title,
  active = false,
  disabled = false,
  accent = false,
  onClick,
}: RingButtonProps): JSX.Element {
  return (
    <button
      type="button"
      className={[
        "bip-ring-btn",
        active ? "bip-ring-btn-active" : "",
        accent ? "bip-ring-btn-accent" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      title={title}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="bip-ring-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="bip-ring-label">{label}</span>
    </button>
  );
}

function RotatingBuilding({ family }: { readonly family: BuildingFamily }): JSX.Element {
  const ref = useRef<Group>(null);
  useFrame((_, dt) => {
    if (ref.current) ref.current.rotation.y += dt * 0.4;
  });
  return (
    <group ref={ref} position={[0, 0, 0]}>
      <BuildingMesh family={family} />
    </group>
  );
}

/* ── Phase 2.B: §5.2 About building level breakdown ─────────────────────── */

interface AboutLevelBreakdownProps {
  readonly family: BuildingFamily;
  readonly currentLevel: number;
  readonly onClose: () => void;
}

function AboutLevelBreakdown({
  family,
  currentLevel,
  onClose,
}: AboutLevelBreakdownProps): JSX.Element {
  const meta = BUILDING_INFO[family];
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const currentRowRef = useRef<HTMLTableRowElement | null>(null);
  const [hoverLevel, setHoverLevel] = useState<number | null>(null);

  const rows = useMemo(() => {
    const out: ReadonlyArray<{
      readonly level: number;
      readonly primary: number;
      readonly secondary: number;
      readonly power: number;
    }> = Array.from({ length: BUILDING_LEVEL_MAX_MVP }, (_, idx) => {
      const level = idx + 1;
      return {
        level,
        primary: buildingPrimaryStat(family, level),
        secondary: buildingSecondaryStat(family, level),
        power: buildingPower(family, level),
      };
    });
    return out;
  }, [family]);

  useEffect(() => {
    const row = currentRowRef.current;
    if (row && tableScrollRef.current) {
      row.scrollIntoView({ block: "center", behavior: "instant" as ScrollBehavior });
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const upgradeCostFromCurrent = (target: number): number | null => {
    if (target <= currentLevel) return null;
    let total = 0;
    for (let l = currentLevel; l < target; l++) {
      total += buildingUpgradeCost(family, ABOUT_BASE_COST, l);
    }
    return total;
  };

  return (
    <div
      className="bip-about-modal"
      onPointerDown={(e) => {
        e.stopPropagation();
        onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bip-about-card bip-about-card-table"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <header className="bip-about-header">
          <h3>About {meta.displayName}</h3>
          <button
            type="button"
            className="bip-close"
            aria-label="Close About"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="bip-about-table-scroll" ref={tableScrollRef}>
          <table className="bip-about-table">
            <thead>
              <tr>
                <th scope="col">Lv</th>
                <th scope="col">{meta.primaryStatLabel}</th>
                <th scope="col">{meta.secondaryStatLabel}</th>
                <th scope="col">Power</th>
                <th scope="col">Defence</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isCurrent = r.level === currentLevel;
                const isFuture = r.level > currentLevel;
                const hovered = hoverLevel === r.level;
                return (
                  <tr
                    key={r.level}
                    ref={isCurrent ? currentRowRef : undefined}
                    className={[
                      isCurrent ? "bip-row-current" : "",
                      isFuture ? "bip-row-future" : "",
                      hovered && isFuture ? "bip-row-hover" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseEnter={() => setHoverLevel(r.level)}
                    onMouseLeave={() =>
                      setHoverLevel((prev) => (prev === r.level ? null : prev))
                    }
                  >
                    <td>Lv {r.level}</td>
                    <td>{formatStat(r.primary)}{meta.primaryStatUnit ? ` ${meta.primaryStatUnit}` : ""}</td>
                    <td>{formatStat(r.secondary)}{meta.secondaryStatUnit ? ` ${meta.secondaryStatUnit}` : ""}</td>
                    <td>{formatStat(r.power)}</td>
                    <td>{meta.defenceMultPct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hoverLevel !== null && hoverLevel > currentLevel ? (
            <UpgradeTooltip
              level={hoverLevel}
              cost={upgradeCostFromCurrent(hoverLevel) ?? 0}
              currentLevel={currentLevel}
            />
          ) : null}
        </div>

        <footer className="bip-about-footer">
          <span className="bip-about-summary">
            Current row highlighted. Hover a future row for upgrade cost preview.
          </span>
          <button type="button" onClick={onClose} className="bip-about-ok">
            OK
          </button>
        </footer>
      </div>
    </div>
  );
}

interface UpgradeTooltipProps {
  readonly level: number;
  readonly cost: number;
  readonly currentLevel: number;
}
function UpgradeTooltip({ level, cost, currentLevel }: UpgradeTooltipProps): JSX.Element {
  return (
    <div className="bip-about-tooltip" role="tooltip" aria-live="polite">
      <span className="bip-about-tooltip-title">
        Lv {currentLevel} → Lv {level}
      </span>
      <span className="bip-about-tooltip-cost">
        {formatStat(cost)} treasury (cumulative)
      </span>
      <span className="bip-about-tooltip-meta">
        Open the Upgrade ring button to start.
      </span>
    </div>
  );
}
